import React, { FC, memo, useState } from 'react';

import { Modal, Input, Button } from 'antd';
import { CopyOutlined, MailOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import tryCopyToClipboard from '@/components/utils/tryCopyToClipboard';

import DiagnosticsList from './DiagnosticsList';
import { buildMailtoHref, buildReportText, ErrorDiagnostics } from './reportText';

import styles from './ReportProblemModal.module.scss';

const NAME_SPACE = 'general.loadErrorReport.modal';

export interface ReportProblemModalProps {
    open: boolean;
    onClose: () => void;
    diagnostics: ErrorDiagnostics;
    /** Comma-joined helpdesk recipient(s) for the mailto "to" field. */
    helpdeskRecipients: string;
}

const ReportProblemModal: FC<ReportProblemModalProps> = ({
    open,
    onClose,
    diagnostics,
    helpdeskRecipients,
}) => {
    const { t } = useTranslation();
    const [note, setNote] = useState<string>('');

    const handleCopyAll = (): void => {
        tryCopyToClipboard(buildReportText(diagnostics, note), t('general.loadErrorReport.modal.detailsLabel'));
    };

    const handleOpenInEmail = (): void => {
        window.location.href = buildMailtoHref(diagnostics, helpdeskRecipients, note);
    };

    const handleAfterClose = (): void => {
        setNote('');
    };

    return (
        <Modal
            className={styles.modal}
            title={t(`${NAME_SPACE}.title`)}
            open={open}
            onCancel={onClose}
            afterClose={handleAfterClose}
            destroyOnClose
            footer={[
                <Button
                    key="copy"
                    icon={<CopyOutlined />}
                    onClick={handleCopyAll}
                >
                    {t(`${NAME_SPACE}.copyAll`)}
                </Button>,
                <Button
                    key="email"
                    icon={<MailOutlined />}
                    onClick={handleOpenInEmail}
                >
                    {t(`${NAME_SPACE}.openInEmail`)}
                </Button>,
                <Button
                    key="done"
                    type="primary"
                    onClick={onClose}
                >
                    {t(`${NAME_SPACE}.done`)}
                </Button>,
            ]}
        >
            <div className={styles.field}>
                <span className={styles.label}>{t(`${NAME_SPACE}.noteLabel`)}</span>
                <Input.TextArea
                    className={styles.note}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder={t(`${NAME_SPACE}.notePlaceholder`)}
                    autoSize={{ minRows: 2, maxRows: 5 }}
                />
            </div>
            <div className={styles.field}>
                <span className={styles.label}>{t(`${NAME_SPACE}.detailsLabel`)}</span>
                <DiagnosticsList diagnostics={diagnostics} />
            </div>
        </Modal>
    );
};

export default memo(ReportProblemModal);
